import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { getCurrentScope, startNewTrace } from '@sentry/react';
import { Link } from 'react-router';
import { getApiErrorMessage } from '@quoruum/frontend-common/api/errorMessage';
import { hotelQueryOptions } from '@quoruum/frontend-common/oas/gen/quoruum/@tanstack/react-query.gen';
import {
  hotelSettingGetSettingsOptions,
  jobActionAsAppendLearnMutation,
  jobActionEnqueueEnqueuerMutation,
  jobActionEnqueueHotelMutation,
  rateScrapeMetadataGetOptions,
} from '@quoruum/frontend-common/oas/gen/admin/@tanstack/react-query.gen';
import {
  LAMBDA_SIZES,
  type LambdaSize,
  BOOKING_SITES,
  isBookingSite,
  type BookingSite,
} from '@quoruum/frontend-common/oas/gen/enumValues.gen';
const SINGLE_ENQUEUE_TYPE_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'appendLearn', label: 'Append Learn' },
];

import { Autocomplete } from '../components/Autocomplete';
import { FreeformMultiSelect } from '../components/FreeformMultiSelect';

type SingleJobType = 'rateShop' | 'learnRateShop' | 'learnHotelMetadata';
type BatchJobType = 'rateShoppingEnqueue' | 'learnRateShoppingEnqueue';
type SingleLambdaSizeOption = LambdaSize | 'AUTO';

const LEARN_JOB_TYPES: ReadonlyArray<SingleJobType> = ['learnRateShop', 'learnHotelMetadata'];

type HotelOption = {
  id: string;
  name: string;
  city?: string;
};

const HOTEL_SEARCH_DEBOUNCE_MS = 250;
const MIN_HOTEL_SEARCH_LENGTH = 2;
const SINGLE_JOB_TYPES: ReadonlyArray<SingleJobType> = [
  'rateShop',
  'learnRateShop',
  'learnHotelMetadata',
];
const BATCH_JOB_TYPES: ReadonlyArray<BatchJobType> = [
  'rateShoppingEnqueue',
  'learnRateShoppingEnqueue',
];
const SCREENSHOT_OPTIONS = ['none', 'manual', 'auto_s3'] as const;
const HEADED_OPTIONS = ['false', 'true', 'REMOTE_CDP'] as const;
const HEADED_NONE_VALUE = '__NONE__';
const CDP_CLUSTER_OPTIONS = ['Prod', 'Test'] as const;

const LLM_CONFIG_FIELDS = [
  { name: 'infer_booking_engine_url', placeholder: 'gpt-5' },
  { name: 'test_booking_engine_url', placeholder: 'gpt-4.1-mini' },
  { name: 'infer_css_selectors', placeholder: 'gpt-5' },
] as const;

type LlmConfigFieldName = (typeof LLM_CONFIG_FIELDS)[number]['name'];
type LlmConfigState = Record<LlmConfigFieldName, string>;

const INITIAL_LLM_CONFIG_STATE: LlmConfigState = {
  infer_booking_engine_url: '',
  test_booking_engine_url: '',
  infer_css_selectors: '',
};

function buildLlmOverride(
  llmConfig: LlmConfigState
): Record<string, { model: string }> | undefined {
  const entries = Object.entries(llmConfig).filter(([, value]) => value.trim());
  if (entries.length === 0) {
    return undefined;
  }

  return Object.fromEntries(entries.map(([name, value]) => [name, { model: value.trim() }]));
}

type ScreenshotOption = (typeof SCREENSHOT_OPTIONS)[number];
type HeadedOption = boolean | 'REMOTE_CDP' | undefined;
type CdpClusterOption = (typeof CDP_CLUSTER_OPTIONS)[number];

type InitialFormState = {
  singleJobType: SingleJobType;
  hotelSearch: string;
  selectedHotel: HotelOption | null;
  bookingSite: BookingSite;
  singleEnqueueType: 'normal' | 'appendLearn';
  headed: HeadedOption;
  screenshot: ScreenshotOption;
  singleLambdaSize: SingleLambdaSizeOption;
  timeout: string;
  pageLoadTimeout: string;
  baseLearnPageLoadTimeout: string;
  stayDate: string;
  cdpCluster: CdpClusterOption;
  debug: boolean;
  dryRun: boolean;
  llmConfig: LlmConfigState;
  batchJobType: BatchJobType;
  hour: string;
  nth: string;
  max: string;
};

function toPrettyJson(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

function toOptionLabel(value: string): string {
  return value;
}

function parseOptionalNumber(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) {
    return undefined;
  }

  return parsed;
}

function parseHeadedParam(value: string | null): HeadedOption {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  if (trimmed === 'REMOTE_CDP') {
    return 'REMOTE_CDP';
  }

  const normalized = trimmed.toLowerCase();
  if (normalized === 'true' || normalized === '1' || normalized === 'yes') {
    return true;
  }

  if (normalized === 'false' || normalized === '0' || normalized === 'no') {
    return false;
  }

  return undefined;
}

function parsePositiveNumberParam(value: string | null): string {
  if (!value) {
    return '';
  }

  const parsed = parseOptionalNumber(value);
  return parsed !== undefined && parsed > 0 ? String(parsed) : '';
}

function parseNonNegativeNumberParam(value: string | null): string {
  if (!value) {
    return '';
  }

  const parsed = parseOptionalNumber(value);
  return parsed !== undefined && parsed >= 0 ? String(parsed) : '';
}

function parseSingleEnqueueTypeParam(value: string | null): 'normal' | 'appendLearn' {
  return value === 'appendLearn' ? value : 'normal';
}

function parseSingleLambdaSizeParam(value: string | null): SingleLambdaSizeOption {
  if (value === 'AUTO') {
    return 'AUTO';
  }

  return LAMBDA_SIZES.includes(value as LambdaSize) ? (value as LambdaSize) : 'AUTO';
}

function parseCdpClusterParam(value: string | null): CdpClusterOption {
  return CDP_CLUSTER_OPTIONS.includes(value as CdpClusterOption)
    ? (value as CdpClusterOption)
    : 'Prod';
}

function parseBooleanParam(value: string | null, defaultValue: boolean): boolean {
  if (value === null) {
    return defaultValue;
  }

  const normalized = value.trim().toLowerCase();
  if (normalized === 'true' || normalized === '1') {
    return true;
  }

  if (normalized === 'false' || normalized === '0') {
    return false;
  }

  return defaultValue;
}

function getInitialFormState(): InitialFormState {
  const searchParams = new URLSearchParams(window.location.search);

  const singleJobTypeParam = searchParams.get('singleJobType');
  const batchJobTypeParam = searchParams.get('batchJobType');
  const screenshotParam = searchParams.get('screenshot');
  const bookingSiteParam = searchParams.get('bookingSite');
  const hotelId = searchParams.get('hotelId')?.trim() ?? '';
  const hotelSearch = searchParams.get('hotelSearch')?.trim() ?? '';

  const singleJobType = SINGLE_JOB_TYPES.includes(singleJobTypeParam as SingleJobType)
    ? (singleJobTypeParam as SingleJobType)
    : 'rateShop';
  const batchJobType = BATCH_JOB_TYPES.includes(batchJobTypeParam as BatchJobType)
    ? (batchJobTypeParam as BatchJobType)
    : 'rateShoppingEnqueue';
  const screenshot = SCREENSHOT_OPTIONS.includes(screenshotParam as ScreenshotOption)
    ? (screenshotParam as ScreenshotOption)
    : 'none';
  const selectedHotel = hotelId
    ? {
        id: hotelId,
        name: hotelSearch || hotelId,
      }
    : null;
  const bookingSite = isBookingSite(bookingSiteParam ?? '')
    ? (bookingSiteParam as BookingSite)
    : 'BRAND';

  return {
    singleJobType,
    hotelSearch: hotelSearch || hotelId,
    selectedHotel,
    bookingSite,
    singleEnqueueType: parseSingleEnqueueTypeParam(searchParams.get('singleEnqueueType')),
    headed: parseHeadedParam(searchParams.get('headed')),
    screenshot,
    singleLambdaSize: parseSingleLambdaSizeParam(searchParams.get('singleLambdaSize')),
    timeout: parsePositiveNumberParam(searchParams.get('timeout')),
    pageLoadTimeout: parsePositiveNumberParam(searchParams.get('pageLoadTimeout')),
    baseLearnPageLoadTimeout: parsePositiveNumberParam(
      searchParams.get('baseLearnPageLoadTimeout')
    ),
    stayDate: searchParams.get('stayDate')?.trim() ?? '',
    cdpCluster: parseCdpClusterParam(searchParams.get('cdpCluster')),
    debug: parseBooleanParam(searchParams.get('debug'), false),
    dryRun: parseBooleanParam(searchParams.get('dryRun'), true),
    llmConfig: LLM_CONFIG_FIELDS.reduce<LlmConfigState>(
      (acc, field) => ({
        ...acc,
        [field.name]: searchParams.get(field.name)?.trim() ?? '',
      }),
      { ...INITIAL_LLM_CONFIG_STATE }
    ),
    batchJobType,
    hour: parseNonNegativeNumberParam(searchParams.get('hour')),
    nth: parseNonNegativeNumberParam(searchParams.get('nth')),
    max: parseNonNegativeNumberParam(searchParams.get('max')),
  };
}

export function EnqueueCenter(): ReactElement {
  const queryClient = useQueryClient();
  const initialFormState = useMemo(() => getInitialFormState(), []);

  const [singleEnqueueType, setSingleEnqueueType] = useState<'normal' | 'appendLearn'>(
    initialFormState.singleEnqueueType
  );
  const [singleLambdaSize, setSingleLambdaSize] = useState<SingleLambdaSizeOption>(
    initialFormState.singleLambdaSize
  );
  const [singleJobType, setSingleJobType] = useState<SingleJobType>(initialFormState.singleJobType);
  const [hotelSearch, setHotelSearch] = useState(initialFormState.hotelSearch);
  const [debouncedHotelSearch, setDebouncedHotelSearch] = useState(initialFormState.hotelSearch);
  const [selectedHotel, setSelectedHotel] = useState<HotelOption | null>(
    initialFormState.selectedHotel
  );
  const [bookingSite, setBookingSite] = useState<BookingSite>(initialFormState.bookingSite);
  const [singleTraceId, setSingleTraceId] = useState('');
  const [singleTraceFollowUrl, setSingleTraceFollowUrl] = useState<string>('');
  const [singleLiveLogUrl, setSingleLiveLogUrl] = useState<string>('');
  const [singleResponse, setSingleResponse] = useState<string>('');
  const [singleError, setSingleError] = useState<string>('');
  const [showBootstrapLearningHint, setShowBootstrapLearningHint] = useState(false);
  const [headed, setHeaded] = useState<HeadedOption>(initialFormState.headed);
  const [screenshot, setScreenshot] = useState<ScreenshotOption>(initialFormState.screenshot);
  const [timeout, setTimeout] = useState(initialFormState.timeout);
  const [pageLoadTimeout, setPageLoadTimeout] = useState(initialFormState.pageLoadTimeout);
  const [baseLearnPageLoadTimeout, setBaseLearnPageLoadTimeout] = useState(
    initialFormState.baseLearnPageLoadTimeout
  );
  const [stayDate, setStayDate] = useState(initialFormState.stayDate);
  const [cdpCluster, setCdpCluster] = useState<CdpClusterOption>(initialFormState.cdpCluster);
  const [debug, setDebug] = useState(initialFormState.debug);
  const [dryRun, setDryRun] = useState(initialFormState.dryRun);
  const [isLlmConfigExpanded, setIsLlmConfigExpanded] = useState(false);
  const [llmConfig, setLlmConfig] = useState<LlmConfigState>(initialFormState.llmConfig);
  const [isFeatureFlagsExpanded, setIsFeatureFlagsExpanded] = useState(false);
  const [featureFlags, setFeatureFlags] = useState<ReadonlyArray<string>>([]);

  const [batchJobType, setBatchJobType] = useState<BatchJobType>(initialFormState.batchJobType);
  const [hour, setHour] = useState(initialFormState.hour);
  const [nth, setNth] = useState(initialFormState.nth);
  const [max, setMax] = useState(initialFormState.max);
  const [batchTraceId, setBatchTraceId] = useState('');
  const [batchTraceFollowUrl, setBatchTraceFollowUrl] = useState<string>('');
  const [batchLiveLogUrl, setBatchLiveLogUrl] = useState<string>('');
  const [batchResponse, setBatchResponse] = useState<string>('');
  const [batchError, setBatchError] = useState<string>('');

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedHotelSearch(hotelSearch.trim());
    }, HOTEL_SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [hotelSearch]);

  useEffect(() => {
    if (singleJobType !== 'learnRateShop' && singleEnqueueType !== 'normal') {
      setSingleEnqueueType('normal');
    }
  }, [singleJobType, singleEnqueueType]);

  useEffect(() => {
    const params = new URLSearchParams();

    params.set('singleJobType', singleJobType);
    params.set('singleEnqueueType', singleEnqueueType);
    if (selectedHotel?.id) {
      params.set('hotelId', selectedHotel.id);
    }
    if (hotelSearch.trim()) {
      params.set('hotelSearch', hotelSearch.trim());
    }
    params.set('bookingSite', bookingSite);
    if (headed !== undefined) {
      params.set('headed', String(headed));
    }
    params.set('screenshot', screenshot);
    params.set('singleLambdaSize', singleLambdaSize);
    if (timeout.trim()) {
      params.set('timeout', timeout.trim());
    }
    if (pageLoadTimeout.trim()) {
      params.set('pageLoadTimeout', pageLoadTimeout.trim());
    }
    if (baseLearnPageLoadTimeout.trim()) {
      params.set('baseLearnPageLoadTimeout', baseLearnPageLoadTimeout.trim());
    }
    if (stayDate.trim()) {
      params.set('stayDate', stayDate.trim());
    }
    params.set('cdpCluster', cdpCluster);
    params.set('debug', String(debug));
    params.set('dryRun', String(dryRun));
    for (const field of LLM_CONFIG_FIELDS) {
      const value = llmConfig[field.name].trim();
      if (value) {
        params.set(field.name, value);
      }
    }
    params.set('batchJobType', batchJobType);
    if (hour.trim()) {
      params.set('hour', hour.trim());
    }
    if (nth.trim()) {
      params.set('nth', nth.trim());
    }
    if (max.trim()) {
      params.set('max', max.trim());
    }

    const search = params.toString();
    const newUrl = `${window.location.pathname}${search ? `?${search}` : ''}`;
    window.history.replaceState(null, '', newUrl);
  }, [
    singleJobType,
    singleEnqueueType,
    selectedHotel,
    hotelSearch,
    bookingSite,
    headed,
    screenshot,
    singleLambdaSize,
    timeout,
    pageLoadTimeout,
    baseLearnPageLoadTimeout,
    stayDate,
    cdpCluster,
    debug,
    dryRun,
    llmConfig,
    batchJobType,
    hour,
    nth,
    max,
  ]);

  const {
    data: hotelSearchResponse,
    isFetching: isHotelSearchFetching,
    isError: hasHotelSearchError,
    error: hotelSearchError,
  } = useQuery({
    ...(debouncedHotelSearch.length >= MIN_HOTEL_SEARCH_LENGTH
      ? hotelQueryOptions({
          query: {
            search: debouncedHotelSearch,
          },
        })
      : hotelQueryOptions()),
    enabled: debouncedHotelSearch.length >= MIN_HOTEL_SEARCH_LENGTH,
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
  });

  const { data: autoRateScrapeMetadata } = useQuery({
    ...rateScrapeMetadataGetOptions({
      path: { externalId: `${bookingSite}:${selectedHotel?.id}` },
    }),
    enabled: singleLambdaSize === 'AUTO' && Boolean(selectedHotel?.id),
    staleTime: 30 * 1000,
  });

  const resolvedSingleLambdaSize: LambdaSize = useMemo(() => {
    if (singleLambdaSize !== 'AUTO') {
      return singleLambdaSize;
    }

    const autoLambdaSize = LEARN_JOB_TYPES.includes(singleJobType)
      ? autoRateScrapeMetadata?.learnLambdaSize
      : autoRateScrapeMetadata?.shopLambdaSize;

    return autoLambdaSize ?? 'NORMAL';
  }, [singleLambdaSize, singleJobType, autoRateScrapeMetadata]);

  const hotelOptions = useMemo<HotelOption[]>(() => {
    const results = hotelSearchResponse?.results ?? [];
    return results
      .filter((hotel) => typeof hotel.id === 'string')
      .map((hotel) => ({
        id: hotel.id as string,
        name: hotel.name ?? 'Unknown Hotel',
        city: hotel.city?.name,
      }))
      .slice(0, 8);
  }, [hotelSearchResponse]);

  // Always use the normal mutation for useMutation, but call the correct one in the handler
  const singleEnqueueHotelMutation = useMutation(jobActionEnqueueHotelMutation());
  const singleAppendLearnMutation = useMutation(jobActionAsAppendLearnMutation());
  const batchEnqueueMutation = useMutation(jobActionEnqueueEnqueuerMutation());

  const hotelMapSettingsLink = useMemo(() => {
    if (!selectedHotel?.id) {
      return '';
    }

    const encodedHotelId = encodeURIComponent(selectedHotel.id);
    return `/tools/hotel-explorer?selectedId=${encodedHotelId}&searchMode=HOTEL&drawerTab=HOTEL_SETTINGS`;
  }, [selectedHotel?.id]);

  async function checkBootstrapLearningOption(hotelId: string): Promise<void> {
    try {
      const settings = await queryClient.fetchQuery(
        hotelSettingGetSettingsOptions({
          path: {
            id: hotelId,
          },
        })
      );
      const hasBootstrapLearningOption =
        settings?.optionsTuples?.some((option) => option.option === 'BOOTSTRAP_LEARNING') ?? false;
      setShowBootstrapLearningHint(!hasBootstrapLearningOption);
    } catch {
      setShowBootstrapLearningHint(false);
    }
  }

  async function handleSingleEnqueue(): Promise<void> {
    if (!selectedHotel?.id) {
      setSingleError('Select a hotel from search results before enqueuing.');
      setShowBootstrapLearningHint(false);
      return;
    }

    const parsedTimeout = parseOptionalNumber(timeout);
    const parsedPageLoadTimeout = parseOptionalNumber(pageLoadTimeout);
    const parsedBaseLearnPageLoadTimeout = parseOptionalNumber(baseLearnPageLoadTimeout);
    if (timeout.trim() && (parsedTimeout === undefined || parsedTimeout <= 0)) {
      setSingleError('Timeout must be a number greater than 0.');
      setShowBootstrapLearningHint(false);
      return;
    }

    if (
      pageLoadTimeout.trim() &&
      (parsedPageLoadTimeout === undefined || parsedPageLoadTimeout <= 0)
    ) {
      setSingleError('Page Load Timeout must be a number greater than 0.');
      setShowBootstrapLearningHint(false);
      return;
    }

    if (
      baseLearnPageLoadTimeout.trim() &&
      (parsedBaseLearnPageLoadTimeout === undefined || parsedBaseLearnPageLoadTimeout <= 0)
    ) {
      setSingleError('Base Learn Page Load Timeout must be a number greater than 0.');
      setShowBootstrapLearningHint(false);
      return;
    }

    setSingleError('');
    setShowBootstrapLearningHint(false);
    setSingleTraceId('');
    setSingleTraceFollowUrl('');
    setSingleLiveLogUrl('');
    setSingleResponse('');

    const llmOverride = buildLlmOverride(llmConfig);
    const featureFlagsOverride = featureFlags.length > 0 ? { enabled: featureFlags } : undefined;

    try {
      const { response, requestTraceId } = await startNewTrace(async () => {
        let response;
        if (singleEnqueueType === 'appendLearn') {
          const arrivalDate = stayDate.trim();
          const departureDate = arrivalDate
            ? new Date(new Date(arrivalDate).getTime() + 24 * 60 * 60 * 1000)
                .toISOString()
                .split('T')[0]
            : '';
          const suffix =
            resolvedSingleLambdaSize === 'NORMAL'
              ? undefined
              : resolvedSingleLambdaSize.toLowerCase();

          response = await singleAppendLearnMutation.mutateAsync({
            body: {
              hotelId: selectedHotel.id,
              type: singleJobType,
              ...(debug ? { debug: true } : {}),
              dryRun,
              ...(arrivalDate ? { arrivalDate } : {}),
              ...(departureDate ? { departureDate } : {}),
              ...(suffix ? { suffix } : {}),
              externalId: `${bookingSite}:${selectedHotel.id}`,
              extra: {
                'lambda.browser.override': {
                  ...(headed !== undefined ? { headed } : {}),
                  ...(screenshot !== 'none' ? { screenshot } : {}),
                  ...(parsedTimeout !== undefined ? { timeout: parsedTimeout } : {}),
                  ...(parsedPageLoadTimeout !== undefined
                    ? { pageLoadTimeout: parsedPageLoadTimeout }
                    : {}),
                  ...(parsedBaseLearnPageLoadTimeout !== undefined
                    ? { baseLearnPageLoadTimeout: parsedBaseLearnPageLoadTimeout }
                    : {}),
                },
                ...(cdpCluster === 'Test' ? { useTestCdpCluster: true } : {}),
                ...(llmOverride ? { 'lambda.llm.override': llmOverride } : {}),
                ...(featureFlagsOverride ? { 'lambda.flags': featureFlagsOverride } : {}),
              },
            },
          });
        } else {
          response = await singleEnqueueHotelMutation.mutateAsync({
            query: {
              hotelId: selectedHotel.id,
              type: singleJobType,
              site: bookingSite,
              ...(stayDate.trim() ? { stayDate: stayDate.trim() } : {}),
            },
            body: {
              lambdaSize: resolvedSingleLambdaSize,
              ...(debug ? { debug: true } : {}),
              dryRun,
              extra: {
                'lambda.browser.override': {
                  ...(headed !== undefined ? { headed } : {}),
                  ...(screenshot !== 'none' ? { screenshot } : {}),
                  ...(parsedTimeout !== undefined ? { timeout: parsedTimeout } : {}),
                  ...(parsedPageLoadTimeout !== undefined
                    ? { pageLoadTimeout: parsedPageLoadTimeout }
                    : {}),
                  ...(parsedBaseLearnPageLoadTimeout !== undefined
                    ? { baseLearnPageLoadTimeout: parsedBaseLearnPageLoadTimeout }
                    : {}),
                },
                ...(cdpCluster === 'Test' ? { useTestCdpCluster: true } : {}),
                ...(llmOverride ? { 'lambda.llm.override': llmOverride } : {}),
                ...(featureFlagsOverride ? { 'lambda.flags': featureFlagsOverride } : {}),
              },
            },
          });
        }
        return {
          response,
          requestTraceId: getCurrentScope().getPropagationContext().traceId,
        };
      });
      if (requestTraceId) {
        setSingleTraceId(requestTraceId);
        setSingleTraceFollowUrl(
          `https://quoruum.sentry.io/explore/logs/trace/${requestTraceId}?tab=logs`
        );
        setSingleLiveLogUrl(
          `https://quoruum.sentry.io/explore/logs/?live=enabled&logsFields=timestamp&logsFields=message&logsQuery=trace%3A${requestTraceId}&logsSortBys=-timestamp&project=-1&statsPeriod=1h`
        );
      }
      setSingleResponse(toPrettyJson(response));
    } catch (error) {
      setSingleError(getApiErrorMessage(error, 'Single enqueue failed.'));

      await checkBootstrapLearningOption(selectedHotel.id);
    }
  }

  async function handleBatchEnqueue(): Promise<void> {
    setBatchError('');
    setBatchTraceId('');
    setBatchTraceFollowUrl('');
    setBatchLiveLogUrl('');
    setBatchResponse('');

    const parsedHour = parseOptionalNumber(hour);
    const parsedNth = parseOptionalNumber(nth);
    const parsedMax = parseOptionalNumber(max);

    if (hour.trim() && parsedHour === undefined) {
      setBatchError('Hour must be a valid number.');
      return;
    }

    if (nth.trim() && parsedNth === undefined) {
      setBatchError('nth must be a valid number.');
      return;
    }

    if (max.trim() && parsedMax === undefined) {
      setBatchError('max must be a valid number.');
      return;
    }

    try {
      const { response, requestTraceId } = await startNewTrace(async () => {
        const response = await batchEnqueueMutation.mutateAsync({
          query: {
            type: batchJobType,
          },
          body: {
            ...(parsedHour !== undefined ? { hour: parsedHour } : {}),
            ...(parsedNth !== undefined ? { nth: parsedNth } : {}),
            ...(parsedMax !== undefined ? { max: parsedMax } : {}),
          },
        });

        return {
          response,
          requestTraceId: getCurrentScope().getPropagationContext().traceId,
        };
      });
      if (requestTraceId) {
        setBatchTraceId(requestTraceId);
        setBatchTraceFollowUrl(
          `https://quoruum.sentry.io/explore/logs/?logsFields=timestamp&logsFields=message&logsQuery=Records%3A%EF%80%8DContains%EF%80%8D${requestTraceId}%20OR%20%28%20%22Job%20completed%22%20AND%20batchTrace%3A%EF%80%8DContains%EF%80%8D${requestTraceId}%20%29&logsSortBys=-timestamp&project=-1&statsPeriod=24h`
        );
        setBatchLiveLogUrl(
          `https://quoruum.sentry.io/explore/logs/?live=enabled&logsFields=timestamp&logsFields=message&logsQuery=trace%3A${requestTraceId}&logsSortBys=-timestamp&project=-1&statsPeriod=1h`
        );
      }
      setBatchResponse(toPrettyJson(response));
    } catch (error) {
      setBatchError(getApiErrorMessage(error, 'Batch enqueue failed.'));
    }
  }

  return (
    <div className="space-y-6">
      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Enqueue Center</h1>
        <p className="mt-2 text-sm text-slate-600">
          Manually enqueue learn and shop jobs for a specific hotel or in batch.
        </p>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-xl font-semibold text-slate-900">Single Enqueue</h2>

        <div className="mt-4 flex flex-wrap items-end gap-4">
          <label className="block text-sm font-medium text-slate-700">
            Job Type
            <select
              className="mt-1 block w-52 max-w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              value={singleJobType}
              onChange={(event) => setSingleJobType(event.target.value as SingleJobType)}
            >
              {SINGLE_JOB_TYPES.map((jobType) => (
                <option key={jobType} value={jobType}>
                  {toOptionLabel(jobType)}
                </option>
              ))}
            </select>
          </label>

          <div className="relative w-96 max-w-full">
            <Autocomplete
              label="Hotel"
              value={hotelSearch}
              placeholder="Search hotel name or id"
              onValueChange={(value) => {
                setHotelSearch(value);
                setSelectedHotel(null);
              }}
              onEscapeKey={() => {
                setHotelSearch('');
                setSelectedHotel(null);
              }}
              options={selectedHotel ? [] : hotelOptions}
              minSearchLength={MIN_HOTEL_SEARCH_LENGTH}
              searchLength={debouncedHotelSearch.length}
              isLoading={isHotelSearchFetching}
              loadingLabel="Searching hotels"
              containerClassName="w-96 max-w-full"
              getOptionKey={(hotel) => hotel.id}
              renderOption={({ option }) => (
                <>
                  <span className="font-medium text-slate-900">{option.name}</span>
                  <span className="ml-2 text-slate-500">
                    {option.city ? `${option.city} - ` : ''}
                    {option.id}
                  </span>
                </>
              )}
              onSelectOption={(hotel) => {
                setSelectedHotel(hotel);
                setHotelSearch(`${hotel.name}${hotel.city ? `, ${hotel.city}` : ''}`);
              }}
            />
          </div>

          <label className="block text-sm font-medium text-slate-700">
            Booking Site
            <select
              className="mt-1 block w-36 max-w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              value={bookingSite}
              onChange={(event) => setBookingSite(event.target.value as BookingSite)}
            >
              {BOOKING_SITES.map((site) => (
                <option key={site} value={site}>
                  {toOptionLabel(site)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="mt-4 flex flex-wrap items-end gap-4">
          {singleJobType === 'learnRateShop' ? (
            <label className="block text-sm font-medium text-slate-700">
              Type
              <select
                className="mt-1 block w-32 max-w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                value={singleEnqueueType}
                onChange={(e) => setSingleEnqueueType(e.target.value as 'normal' | 'appendLearn')}
              >
                {SINGLE_ENQUEUE_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <label className="block text-sm font-medium text-slate-700">
            Headed
            <select
              className="mt-1 block w-40 max-w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              value={headed === undefined ? HEADED_NONE_VALUE : String(headed)}
              onChange={(event) => {
                const value = event.target.value;
                if (value === HEADED_NONE_VALUE) {
                  setHeaded(undefined);
                  return;
                }

                if (value === 'REMOTE_CDP') {
                  setHeaded(value);
                  return;
                }

                setHeaded(value === 'true');
              }}
            >
              <option value={HEADED_NONE_VALUE}>-- None --</option>
              {HEADED_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm font-medium text-slate-700">
            Screenshot
            <select
              className="mt-1 block w-40 max-w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              value={screenshot}
              onChange={(event) =>
                setScreenshot(event.target.value as 'none' | 'manual' | 'auto_s3')
              }
            >
              {SCREENSHOT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {toOptionLabel(option)}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm font-medium text-slate-700">
            Lambda Size
            <select
              className="mt-1 block w-36 max-w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              value={singleLambdaSize}
              onChange={(event) =>
                setSingleLambdaSize(event.target.value as SingleLambdaSizeOption)
              }
            >
              <option value="AUTO">Auto</option>
              {LAMBDA_SIZES.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm font-medium text-slate-700">
            Timeout (optional)
            <input
              type="number"
              min={1}
              className="mt-1 block w-32 max-w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              value={timeout}
              onChange={(event) => setTimeout(event.target.value)}
              placeholder="e.g. 20000"
            />
          </label>

          <label className="block text-sm font-medium text-slate-700">
            Page Load Timeout (optional)
            <input
              type="number"
              min={1}
              className="mt-1 block w-40 max-w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              value={pageLoadTimeout}
              onChange={(event) => setPageLoadTimeout(event.target.value)}
              placeholder="e.g. 3000"
            />
          </label>

          <label className="block text-sm font-medium text-slate-700">
            Base Learn Page Load Timeout (optional)
            <input
              type="number"
              min={1}
              className="mt-1 block w-40 max-w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              value={baseLearnPageLoadTimeout}
              onChange={(event) => setBaseLearnPageLoadTimeout(event.target.value)}
              placeholder="e.g. 6000"
            />
          </label>

          <label className="block text-sm font-medium text-slate-700">
            Stay Date (optional)
            <input
              type="date"
              className="mt-1 block w-40 max-w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              value={stayDate}
              onChange={(event) => setStayDate(event.target.value)}
            />
          </label>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-4">
          <label className="block text-sm font-medium text-slate-700">
            CDP Cluster
            <select
              className="mt-1 block w-36 max-w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              value={cdpCluster}
              onChange={(event) => setCdpCluster(event.target.value as CdpClusterOption)}
            >
              {CDP_CLUSTER_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>

          <div className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                checked={debug}
                onChange={(event) => setDebug(event.target.checked)}
              />
              Debug
            </label>
            <span className="group relative inline-flex">
              <button
                type="button"
                className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-sky-600 text-[10px] font-bold leading-none text-white focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-1"
                aria-label="Debug info"
              >
                i
              </button>
              <span
                role="tooltip"
                className="pointer-events-none invisible absolute left-1/2 top-6 z-20 w-72 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-xs font-normal leading-5 text-white opacity-0 shadow-lg transition group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
              >
                Force the job to run even if there are milestones that would normally prevent
                execution
              </span>
            </span>
          </div>

          <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
              checked={dryRun}
              onChange={(event) => setDryRun(event.target.checked)}
            />
            Dry Run
          </label>
        </div>

        {cdpCluster === 'Test' ? (
          <p className="mt-3 text-sm text-amber-800">
            Make sure you have added an instance to the AWS Target group{' '}
            <a
              className="text-sky-700 underline decoration-sky-300 underline-offset-2 hover:text-sky-800"
              href="https://us-east-1.console.aws.amazon.com/ec2/home?region=us-east-1#TargetGroup:targetGroupArn=arn:aws:elasticloadbalancing:us-east-1:337909779458:targetgroup/cdp-test/ec40a9442f649b86"
              target="_blank"
              rel="noreferrer"
            >
              here
            </a>{' '}
            before enqueueing.
          </p>
        ) : null}

        <div className="mt-3">
          <button
            type="button"
            className="flex items-center gap-1 text-sm font-medium text-slate-700"
            onClick={() => setIsLlmConfigExpanded((expanded) => !expanded)}
            aria-expanded={isLlmConfigExpanded}
          >
            <span>{isLlmConfigExpanded ? '▾' : '▸'}</span>
            LLM Config
          </button>

          {isLlmConfigExpanded ? (
            <div className="mt-2 flex flex-wrap items-end gap-4">
              {LLM_CONFIG_FIELDS.map((field) => (
                <label key={field.name} className="block text-sm font-medium text-slate-700">
                  {field.name}
                  <input
                    type="text"
                    className="mt-1 block w-48 max-w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                    value={llmConfig[field.name]}
                    onChange={(event) =>
                      setLlmConfig((current) => ({
                        ...current,
                        [field.name]: event.target.value,
                      }))
                    }
                    placeholder={field.placeholder}
                  />
                </label>
              ))}
            </div>
          ) : null}
        </div>

        <div className="mt-3">
          <button
            type="button"
            className="flex items-center gap-1 text-sm font-medium text-slate-700"
            onClick={() => setIsFeatureFlagsExpanded((expanded) => !expanded)}
            aria-expanded={isFeatureFlagsExpanded}
          >
            <span>{isFeatureFlagsExpanded ? '▾' : '▸'}</span>
            Feature Flags
          </button>

          {isFeatureFlagsExpanded ? (
            <div className="mt-2 flex flex-wrap items-end gap-4">
              <FreeformMultiSelect
                label="Feature Flags"
                values={featureFlags}
                onValuesChange={setFeatureFlags}
                containerClassName="shrink-0 min-w-[16rem] flex flex-col text-sm font-medium normal-case tracking-normal text-slate-700"
                emptyLabel="None"
                inputPlaceholder="Type a flag and press Enter"
              />
            </div>
          ) : null}
        </div>

        <div className="mt-3 flex flex-wrap items-end gap-4">
          <button
            type="button"
            className="rounded-md bg-sky-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-400"
            onClick={() => {
              void handleSingleEnqueue();
            }}
            disabled={
              singleEnqueueType === 'appendLearn'
                ? singleAppendLearnMutation.isPending
                : singleEnqueueHotelMutation.isPending
            }
          >
            {singleEnqueueType === 'appendLearn'
              ? singleAppendLearnMutation.isPending
                ? 'Enqueuing...'
                : 'Enqueue'
              : singleEnqueueHotelMutation.isPending
                ? 'Enqueuing...'
                : 'Enqueue'}
          </button>
          <span className="text-sm text-slate-600 self-center">
            Selected hotel id: {selectedHotel?.id ?? 'none'}
          </span>
        </div>

        {hasHotelSearchError ? (
          <p className="mt-3 text-sm text-red-700">
            Hotel search failed: {getApiErrorMessage(hotelSearchError, 'Unknown error')}
          </p>
        ) : null}

        {singleError ? <p className="mt-3 text-sm text-red-700">{singleError}</p> : null}

        {singleError && showBootstrapLearningHint && hotelMapSettingsLink ? (
          <p className="mt-1 text-sm text-slate-600">
            Do you need to enable BOOTSTRAP_LEARNING for this{' '}
            <Link
              className="text-sky-700 underline decoration-sky-300 underline-offset-2 hover:text-sky-800"
              to={hotelMapSettingsLink}
            >
              hotel
            </Link>
            ?
          </p>
        ) : null}

        {singleTraceFollowUrl ? (
          <p className="mt-4 text-sm text-slate-700">
            Follow along at{' '}
            {singleTraceId ? (
              <>
                <Link
                  className="text-sky-700 underline decoration-sky-300 underline-offset-2 hover:text-sky-800"
                  to={`/tools/trace-viewer?traceId=${encodeURIComponent(singleTraceId)}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  View Trace
                </Link>{' '}
                ·{' '}
              </>
            ) : null}
            <a
              className="text-sky-700 underline decoration-sky-300 underline-offset-2 hover:text-sky-800"
              href={singleTraceFollowUrl}
              target="_blank"
              rel="noreferrer"
            >
              Sentry Trace
            </a>
            {singleLiveLogUrl ? (
              <>
                {' '}
                ·{' '}
                <a
                  className="text-sky-700 underline decoration-sky-300 underline-offset-2 hover:text-sky-800"
                  href={singleLiveLogUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Live Log
                </a>
              </>
            ) : null}
          </p>
        ) : null}

        {singleResponse ? (
          <pre className="mt-2 overflow-auto rounded-md bg-slate-900 p-4 text-xs text-slate-100">
            {singleResponse}
          </pre>
        ) : null}
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-xl font-semibold text-slate-900">Batch Enqueue</h2>

        <div className="mt-4 flex flex-wrap items-end gap-4">
          <label className="block text-sm font-medium text-slate-700">
            Job Type
            <select
              className="mt-1 block w-64 max-w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              value={batchJobType}
              onChange={(event) => setBatchJobType(event.target.value as BatchJobType)}
            >
              {BATCH_JOB_TYPES.map((jobType) => (
                <option key={jobType} value={jobType}>
                  {toOptionLabel(jobType)}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm font-medium text-slate-700">
            Hour (optional)
            <input
              type="number"
              min={0}
              className="mt-1 block w-28 max-w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              value={hour}
              onChange={(event) => setHour(event.target.value)}
              placeholder="e.g. 14"
            />
          </label>

          <label className="block text-sm font-medium text-slate-700">
            nth (optional)
            <input
              type="number"
              min={0}
              className="mt-1 block w-28 max-w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              value={nth}
              onChange={(event) => setNth(event.target.value)}
              placeholder="e.g. 10"
            />
          </label>

          <label className="block text-sm font-medium text-slate-700">
            max (optional)
            <input
              type="number"
              min={0}
              className="mt-1 block w-28 max-w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              value={max}
              onChange={(event) => setMax(event.target.value)}
              placeholder="e.g. 500"
            />
          </label>
        </div>

        <div className="mt-4">
          <button
            type="button"
            className="rounded-md bg-sky-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-400"
            onClick={() => {
              void handleBatchEnqueue();
            }}
            disabled={batchEnqueueMutation.isPending}
          >
            {batchEnqueueMutation.isPending ? 'Enqueuing...' : 'Enqueue'}
          </button>
        </div>

        {batchError ? <p className="mt-3 text-sm text-red-700">{batchError}</p> : null}

        {batchTraceFollowUrl ? (
          <p className="mt-4 text-sm text-slate-700">
            Follow along at{' '}
            {batchTraceId ? (
              <>
                <Link
                  className="text-sky-700 underline decoration-sky-300 underline-offset-2 hover:text-sky-800"
                  to={`/tools/trace-viewer?traceId=${encodeURIComponent(batchTraceId)}`}
                >
                  View Trace
                </Link>{' '}
                ·{' '}
              </>
            ) : null}
            <a
              className="text-sky-700 underline decoration-sky-300 underline-offset-2 hover:text-sky-800"
              href={batchTraceFollowUrl}
              target="_blank"
              rel="noreferrer"
            >
              Sentry Trace
            </a>
            {batchLiveLogUrl ? (
              <>
                {' '}
                ·{' '}
                <a
                  className="text-sky-700 underline decoration-sky-300 underline-offset-2 hover:text-sky-800"
                  href={batchLiveLogUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Live Log
                </a>
              </>
            ) : null}
          </p>
        ) : null}

        {batchResponse ? (
          <pre className="mt-4 overflow-auto rounded-md bg-slate-900 p-4 text-xs text-slate-100">
            {batchResponse}
          </pre>
        ) : null}
      </section>
    </div>
  );
}
